import { useQuery, useMutation } from '@apollo/client';
import { useTheme } from 'styled-components';
import { Formik } from 'formik';
import Button from '../components/Button';
import { Field, FieldInput, FieldLabel } from '../components/Field';
import Loader from '../components/Loader';
import { Modal, ModalName, ModalPart } from '../components/Modal';
import { useToast } from '../components/Toast';
import { SPACE, VIEWER } from '../utilities/queries';
import { LEAVE_SPACE } from '../utilities/mutations';
import { useSpaceContext } from '../contexts/space';

const LeaveSpaceModal = ({ space, onClose }) => {
  const { spaceVars } = useSpaceContext();
  const { loading, data } = useQuery(SPACE, {
    variables: { slug: space.slug, ...spaceVars },
  });
  const [leaveSpace] = useMutation(LEAVE_SPACE, {
    variables: { slug: space.slug },
    update: (cache) => {
      const data = cache.readQuery({
        query: VIEWER,
      });
      const viewer = {
        ...data.viewer,
        spaces: data.viewer.spaces.filter(({ slug }) => space.slug !== slug),
      };
      cache.writeQuery({
        query: VIEWER,
        data: { viewer },
      });
    },
  });
  const theme = useTheme();
  const toast = useToast();

  return (
    <Modal onClose={() => onClose()} isDanger>
      {loading && !data ? (
        <ModalPart>
          <Loader />
        </ModalPart>
      ) : (
        <>
          <ModalName>Leave space</ModalName>
          <Formik
            initialValues={{ name: '' }}
            onSubmit={async (values) => {
              if (values.name !== data.space.name) {
                toast({ children: 'Name is invalid' });
              } else {
                try {
                  await leaveSpace();
                  onClose();
                } catch (error) {
                  toast({ children: error.message });
                }
              }
            }}
          >
            {({
              values,
              handleChange,
              handleBlur,
              handleSubmit,
              isSubmitting,
            }) => (
              <form onSubmit={handleSubmit}>
                <ModalPart>
                  <div
                    style={{
                      color: theme.alias.subtle,
                      lineHeight: '20px',
                      fontWeight: 500,
                    }}
                  >
                    This will instantly remove you from the {space.name} space.
                    Your topics and comments will remain.
                  </div>
                  <Field>
                    <FieldLabel>
                      Confirm the space name ({data.space.name})
                    </FieldLabel>
                    <FieldInput
                      type="text"
                      name="name"
                      onChange={handleChange}
                      onBlur={handleBlur}
                      value={values.name}
                      data-autofocus
                    />
                  </Field>
                </ModalPart>
                <ModalPart>
                  <Button type="button" onClick={() => onClose()} isExtended>
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    disabled={isSubmitting || values.name !== data.space.name}
                    isDanger
                    isExtended
                  >
                    Leave
                  </Button>
                </ModalPart>
              </form>
            )}
          </Formik>
        </>
      )}
    </Modal>
  );
};

export default LeaveSpaceModal;
